import { type ReportListResponse, type Report, type GetReportsRequest, type DueDateReport, ReportColumn, ReportsType } from '@/models/report';
import {
  generateParams,
  useGetPaged,
  useGet,
  usePut,
  useDelete,
  usePost,
  unpackMappedResponseWithKey,
  useDownload,
  unpackMappedResponse
} from '../ApiService';
import { API_ROUTES } from '@/constants/apiRoutes';
import { ExportReportFileValue, ExportReportFormFileLabel } from '@/constants/exportType';
import {
  compareObjectsByFields,
  filterArrayByKey,
  filterDuplicateValuesFromArray,
  getObjectKeys,
  parseJson
} from '@/utils/objectManipulation';
import { RequestHeaders } from '@/constants/requestHeaders';
import { downloadFile, generateDownloadResponse } from '../DownloadService';
import type {
  ReportNote,
  RestrictNoteRequest,
  ReportForm,
  GetReportResponse,
  ReportDetails,
  AddToSelection,
  RemoveFromSelection,
  SelectionChangeResponse,
  ActivitiesResponse,
  ReportActivity,
  ActivityDetailsParams,
  ReportActivityEntries,
  ReportActivityEntryResponse,
  AutomaticProcessingCodeEntries,
  AutomaticProcessingCodeEntry,
  FileEntries,
  FileEntry,
  ReportEntry,
  DeleteFile,
  ReportValidation,
  GetValidations,
  FormAutomaticProcessingCodeResponse,
  BaseExportOptions,
  ExportReportOptions,
  ExportReportFormOptions,
  ExportReportQueryParams,
  AopValidationValues,
  ProjectForm,
  ApproveReport,
  ReportHelpingRequest,
  CreateAggregatedReport,
  CreateAggregatedReportResponse,
  ReportFileResponse,
  GetReportFilesRequest,
  ReportFilesData,
  GetActivitiesRequest,
  ExportResponse,
  GetFormAutomaticProcessingCodesRequest,
  ReportFile,
  FormAutomaticProcessingCode,
  ChangeSelection,
  ReportNoteResponse,
  EditReportNoteRequest,
  CreateReportNoteRequest,
  GetReportNotesResponse,
  AopValidationValuesResponse,
  AopCalculation,
  AopCalculationResponse,
  ImportReport,
  CreateReportResponse,
  JsonFormHistory,
  JsonFormHistoryResponse,
  UpdateReportPayload,
  GetFormAutomaticProcessingCodesHistoryRequest,
  JsonReportFormResponse,
  PositionEntry,
  PositionEntries,
  UpdateReportJsonForm,
  ModifyProjectResponse
} from '@/services/report/models';
import type { User } from '../security/models';
import { RequestStatus, type MappedResponse, type PaginatedRequest } from '@/core/api/types';
import { ActivityEntryType } from '@/components/report-activity/constants/activityEntryType';
import { SortDirection } from '@/constants/sortDirection';
import { isEoForm3TotalRow, type EoForm, type EoFormSubTableData, type EoFormSubTableDataWithParentId } from './models/eoForm';
import { getEoFormPositionLabelForActivities } from '@/components/report-forms/utils/positionLabelHelpers';
import { ReportActivityValue } from '@/components/report-activity/constants/activityTypes';
import { getJsonConfigForFormId, mapJsonConfiguration } from './report-form-configuration/jsonConfigMapper';

export async function getReports(
  reportsType: 'ownReports' | 'allReports',
  reportsPeriodType: 'active' | 'historic',
  payload: GetReportsRequest = { perPage: 10, page: 1, sortBy: ReportColumn.Status, sortDesc: SortDirection.Asc }
): Promise<MappedResponse<Report[]>> {
  const result = await useGetPaged<ReportListResponse, GetReportsRequest>(
    API_ROUTES.Reports.GetReports(reportsPeriodType, reportsType),
    payload
  );
  return unpackMappedResponseWithKey(result, 'reports');
}

export async function getChildOrganizationReports(
  reportsPeriodType: 'active' | 'historic',
  payload: GetReportsRequest = { perPage: 10, page: 1, sortBy: ReportColumn.Status, sortDesc: SortDirection.Asc }
): Promise<MappedResponse<Report[]>> {
  const result = await useGetPaged<ReportListResponse, GetReportsRequest>(
    API_ROUTES.Reports.GetChildReports(reportsPeriodType),
    payload
  );
  return unpackMappedResponseWithKey(result, 'reports');
}

export async function getReport(reportName: string): Promise<MappedResponse<ReportDetails>> {
  const result = await useGet<GetReportResponse>(API_ROUTES.Reports.GetReport(reportName));

  const unpacked = unpackMappedResponseWithKey(result, 'report');
  return {
    ...unpacked,
    data: unpacked.data ? await mapJsonConfiguration(unpacked.data) : null
  } as MappedResponse<ReportDetails>;
}

export async function getReportDigitalSignatureTemplate(
  reportName: string
): Promise<MappedResponse<ExportResponse>> {
  const result = await useDownload<Blob>(
    API_ROUTES.Reports.GetReportDigitalSignatureTemplate(reportName)
  );
  if (result.status === RequestStatus.SUCCESS) {
    return {
      data: generateDownloadResponse(result.data, result.networkResponse),
      status: result.status
    };
  }
  return { data: null, status: RequestStatus.ERROR };
}

export async function exportReport(
  options: ExportReportOptions
): Promise<MappedResponse<ExportResponse>> {
  const params = setExportParams(options);
  const responseType
    = options.reportFileType === 'pdf'
      ? 'arraybuffer'
      : options.reportFileType === 'xlsx'
        ? 'blob'
        : 'json';
  const result = await useDownload(
    API_ROUTES.Reports.ExportReport(options.reportName, options.reportFileType),
    {
      responseType,
      headers: {
        [RequestHeaders.UseReportingUrl]: 'true'
      }
    },
    params
  );
  if (result.status === RequestStatus.SUCCESS) {
    const formatJson = options.reportFileType === ExportReportFileValue.Json;
    const data = generateDownloadResponse(result.data, result.networkResponse, formatJson);
    return {
      data,
      status: RequestStatus.SUCCESS
    };
  } else {
    return {
      data: null,
      status: RequestStatus.ERROR
    };
  }
}

export async function exportReportForm(
  options: ExportReportFormOptions
): Promise<MappedResponse<ExportResponse>> {
  const params = setExportParams(options);
  const responseType
    = options.reportFileType === ExportReportFormFileLabel.Pdf
      ? 'arraybuffer'
      : options.reportFileType === ExportReportFormFileLabel.Excel
        ? 'blob'
        : 'json';
  const result = await useDownload(
    API_ROUTES.Reports.ExportReportForm(options.reportName, options.reportFileType, options.formId, options.formKind),
    {
      responseType,
      headers: {
        [RequestHeaders.UseReportingUrl]: 'true'
      }
    },
    params
  );
  if (result.status === RequestStatus.ERROR) {
    return {
      data: null,
      status: RequestStatus.ERROR
    };
  }
  const formatJson = options.reportFileType === ExportReportFormFileLabel.Json;
  const data = generateDownloadResponse(result.data, result.networkResponse, formatJson);
  return {
    data,
    status: RequestStatus.SUCCESS
  };
}

export function setExportParams(options: BaseExportOptions): ExportReportQueryParams {
  const checkedFields = getObjectKeys(options).filter(
    (key: string): key is keyof ExportReportQueryParams =>
      key !== 'reportName'
      && key !== 'reportFileType'
      && options[key as keyof BaseExportOptions] !== undefined
  );
  const params: ExportReportQueryParams = {};
  checkedFields.forEach((key: keyof ExportReportQueryParams): void => {
    params[key] = options[key];
  });
  return params;
}

export async function getFormAutomaticProcessingCodes(
  payload: GetFormAutomaticProcessingCodesRequest
): Promise<MappedResponse<FormAutomaticProcessingCodeResponse>> {
  const result = await useGet<
    FormAutomaticProcessingCodeResponse,
    GetFormAutomaticProcessingCodesRequest
  >(API_ROUTES.Reports.GetFormAutomaticProcessingCodes(payload.reportName, payload.formId), {
    ...payload,
    perPage: payload.perPage || undefined,
    page: payload.page || undefined
  });
  return unpackMappedResponse(result);
}

export async function getFormAutomaticProcessingCodesHistory(
  payload: GetFormAutomaticProcessingCodesHistoryRequest
): Promise<MappedResponse<FormAutomaticProcessingCodeResponse>> {
  const result = await useGet<
    FormAutomaticProcessingCodeResponse,
    { dateFrom: string }
  >(API_ROUTES.Reports.GetFormAutomaticProcessingCodesHistory(payload.reportName, payload.formId), {
    dateFrom: payload.dateFrom
  });
  return unpackMappedResponse(result);
}

export async function getJsonForm(
  payload: GetFormAutomaticProcessingCodesRequest
): Promise<MappedResponse<JsonReportFormResponse>> {
  const result = await useGet<
    JsonReportFormResponse,
    GetFormAutomaticProcessingCodesRequest
  >(API_ROUTES.Reports.GetJsonForm(payload.reportName, payload.formId), {
    ...payload,
    perPage: payload.perPage || undefined,
    page: payload.page || undefined
  });
  const unpacked = unpackMappedResponse(result);
  return {
    ...unpacked,
    data: unpacked.data ? { ...unpacked.data, form: await getJsonConfigForFormId(unpacked.data.form) } : null
  } as MappedResponse<JsonReportFormResponse>;
}

export function addParentIdToSubTableData(data: EoForm[]): EoForm[] {
  return data.map((row: EoForm): EoForm => !row.subTableData
    ? row
    : ({
        ...row,
        subTableData: row.subTableData?.map((subRow: EoFormSubTableData): EoFormSubTableDataWithParentId => ({
          ...subRow,
          parentId: row.id
        }) as EoFormSubTableDataWithParentId)
      }) as EoForm);
}

export async function getAutomaticProcessingCodeHistory(
  reportName: string,
  formId: number,
  code: number
): Promise<FormAutomaticProcessingCode[]> {
  const result = await useGet<{ automaticProcessingCodes: FormAutomaticProcessingCode[] }>(
    API_ROUTES.Reports.GetAutomaticProcessingCodeHistory(reportName, formId, code)
  );
  return result.data?.automaticProcessingCodes || [];
}

export async function getActivities(
  reportName: string,
  params?: GetActivitiesRequest
): Promise<ReportActivity[]> {
  const result = await useGetPaged<ActivitiesResponse, GetActivitiesRequest>(
    API_ROUTES.Reports.GetActivities(reportName),
    {
      perPage: 10,
      page: 1,
      ...params
    }
  );
  return result.data?.activities || [];
}

export async function getActivityUsers(reportName: string): Promise<User[]> {
  const result = await useGet<{ users: User[] }, ActivityDetailsParams>(
    API_ROUTES.Reports.GetActivityUsers(reportName)
  );
  return result.data?.users || [];
}

export async function getActivityDetails(
  reportName: string,
  params: ActivityDetailsParams,
  type: ActivityEntryType
): Promise<ReportActivityEntries> {
  const result = await useGet<ReportActivityEntryResponse, ActivityDetailsParams>(
    API_ROUTES.Reports.GetActivityDetails(reportName),
    params
  );
  const data = result.data || [];
  const entries = mapActivityEntries(data, type, !!params.activityJsonFormId);
  return entries;
}

export function mapActivityEntries(
  response: ReportActivityEntryResponse,
  type: ActivityEntryType,
  isJsonForm: boolean
): ReportActivityEntries {
  if (type === ActivityEntryType.AutomaticProcessingCode) {
    return mapAutomaticProcessingCodeEntries(response as AutomaticProcessingCodeEntry[], isJsonForm);
  }
  if (type === ActivityEntryType.Report) {
    return mapReportEntries(response.reverse() as ReportEntry[]);
  }
  if (type === ActivityEntryType.File) {
    return mapFileEntries(response.reverse() as FileEntry[]);
  }
  if (type === ActivityEntryType.Position) {
    return mapPositionEntries(response as PositionEntry[]);
  }
  throw new Error('Invalid activity type');
}

export function mapAutomaticProcessingCodeEntries(
  data: AutomaticProcessingCodeEntry[],
  isJsonForm: boolean
): AutomaticProcessingCodeEntries {
  if (isJsonForm) {
    const afterChange = data.filter((d) => d.row_type === 'current');
    const beforeChange = data.filter((d) => d.row_type === 'historical');
    const entries = [beforeChange, afterChange];
    return {
      type: ActivityEntryType.AutomaticProcessingCode,
      entries
    };
  }
  const afterChange = data.slice(0, Math.floor(data.length / 2));
  const beforeChange = data.slice(Math.floor(data.length / 2));
  const afterChangeFiltered = filterDuplicateValuesFromArray(afterChange, beforeChange);
  const beforeChangeFiltered = filterArrayByKey(
    beforeChange,
    afterChangeFiltered,
    'automatic_processing_code'
  );

  const entries = [beforeChangeFiltered, afterChangeFiltered];
  return {
    type: ActivityEntryType.AutomaticProcessingCode,
    entries
  };
}

export function mapPositionEntries(
  data: PositionEntry[]
): PositionEntries {
  const entriesWithActivityType = getPositionEntriesWithActivityType(data);
  const position = getPositionForActivityEntries(entriesWithActivityType.activityType, entriesWithActivityType.entries);
  const getSubtableFields = subtableFieldsExist(entriesWithActivityType.activityType, entriesWithActivityType.entries);
  return {
    type: ActivityEntryType.Position,
    ...entriesWithActivityType,
    position,
    getSubtableFields
  };
}

export function subtableFieldsExist(activityType: ReportActivityValue, entries: PositionEntry[][]): boolean {
  if (activityType !== ReportActivityValue.POSITION_UPDATED) {
    return false;
  }
  const activity = entries[1];
  if (!activity[0]?.position) {
    return false;
  }
  return isEoForm3TotalRow(activity[0].position);
}

export function getPositionForActivityEntries(activityType: ReportActivityValue, entries: PositionEntry[][]): string {
  const activity = activityType === ReportActivityValue.POSITION_UPDATED ? entries[1] : entries[0];
  if (activity[0]?.position) {
    return getEoFormPositionLabelForActivities(activity[0].position);
  }
  return '';
}

export function getPositionEntriesWithActivityType(entry: PositionEntry[]): { entries: PositionEntry[][]; activityType: ReportActivityValue } {
  const created = entry.filter((d) => d.row_type === 'created').map((d) => ({ ...d, position: !d.data ? null : parseJson<EoForm>(d.data) }));
  if (created.length) {
    return { entries: [created], activityType: ReportActivityValue.POSITION_CREATED };
  }
  const deleted = entry.filter((d) => d.row_type === 'deleted').map((d) => ({ ...d, position: !d.data ? null : parseJson<EoForm>(d.data) }));
  if (deleted.length) {
    return { entries: [deleted], activityType: ReportActivityValue.POSITION_DELETED };
  }
  const afterChange = entry.filter((d) => d.row_type === 'current').map((d) => ({ ...d, position: !d.data ? null : parseJson<EoForm>(d.data) }));
  const beforeChange = entry.filter((d) => d.row_type === 'historical').map((d) => ({ ...d, position: !d.data ? null : parseJson<EoForm>(d.data) }));
  const entries = [beforeChange, afterChange];
  return { entries, activityType: ReportActivityValue.POSITION_UPDATED };
}

export function mapReportEntries(entries: ReportEntry[]): ReportActivityEntries {
  const dateChanged
    = entries.length > 1
      && entries[0].report_organization_status_changed_date
      !== entries[1].report_organization_status_changed_date;
  return {
    type: ActivityEntryType.Report,
    entries,
    dateChanged
  };
}

export function mapFileEntries(entries: FileEntry[]): FileEntries {
  return {
    type: ActivityEntryType.File,
    entries
  };
}

export async function getFormValidations(reportName: string): Promise<{ validations: ReportValidation[]; report: ReportDetails | null }> {
  const result = await useGet<GetValidations>(API_ROUTES.Reports.GetFormValidations(reportName));
  return result.data ?? { validations: [] as ReportValidation[], report: null };
}

export async function getReportFiles(
  reportName: string,
  params: GetReportFilesRequest
): Promise<ReportFilesData> {
  const result = await useGetPaged<ReportFileResponse, GetReportFilesRequest>(
    API_ROUTES.Files.GetReportFiles(reportName),
    params
  );
  const files = result.data?.files || [];
  const remainingChanges = Number(result.networkResponse?.data.additionalInformation);
  return {
    files,
    remainingChanges
  };
}

export async function getReportDigitalSignature(
  reportName: string,
  file: ReportFile
): Promise<void> {
  const result = await useDownload<Blob, never>(
    API_ROUTES.Files.GetReportSignatureByFileId(reportName, file.id),
    { responseType: 'blob' }
  );
  if (result.status === RequestStatus.ERROR) {
    return;
  }

  const headers = result.networkResponse.headers;
  const blob = new Blob([result.data as Blob], {
    type: headers['content-type'] as string
  });
  downloadFile(blob, file.title + file.extension);
}

export async function getReportFileById(reportName: string, file: ReportFile): Promise<void> {
  const result = await useDownload<Blob>(
    API_ROUTES.Reports.GetReportFileById(reportName, file.id),
    {
      headers: {
        [RequestHeaders.UseReportingUrl]: 'true'
      }
    }
  );
  if (result.status === RequestStatus.ERROR) {
    return;
  }
  const download = generateDownloadResponse(result.data, result.networkResponse, true);

  downloadFile(download.blob, download.fileName);
}

export async function downloadReportFile(reportName: string, file: ReportFile): Promise<void> {
  const response = await useDownload<Blob>(
    API_ROUTES.Files.DownloadReportFile(reportName, file.id + ''),
    { responseType: 'arraybuffer' }
  );
  if (response.status === RequestStatus.ERROR) {
    return;
  }
  const headers = response.networkResponse.headers;
  const blob = new Blob([response.data as BlobPart], {
    type: headers['content-type'] as string
  });
  downloadFile(blob, file.title + file.extension);
}

export async function downloadAllReportFiles(reportName: string): Promise<void> {
  const response = await useDownload<Blob>(API_ROUTES.Reports.DownloadReportFiles(reportName), {
    responseType: 'arraybuffer',
    headers: {
      [RequestHeaders.UseReportingUrl]: 'true'
    }
  });
  if (response.status === RequestStatus.ERROR) {
    return;
  }
  const download = generateDownloadResponse(
    response.data,
    response.networkResponse,
    false,
    'application/octet-stream'
  );
  downloadFile(download.blob, download.fileName);
}

export async function deleteReportFile(
  fileId: number,
  data: DeleteFile
): Promise<MappedResponse<ReportFile>> {
  const response = await useDelete<ReportFile>(API_ROUTES.Files.DeleteReportFile(fileId), { data });
  return unpackMappedResponse(response);
}

export async function exportReportActivities(
  reportName: string,
  params: GetActivitiesRequest
): Promise<void> {
  const response = await useDownload<string, GetActivitiesRequest>(
    API_ROUTES.Reports.ExportActivities(reportName),
    {
      headers: {
        [RequestHeaders.UseReportingUrl]: 'true'
      }
    },
    params
  );
  if (response.status === RequestStatus.ERROR) {
    return;
  }
  const download = generateDownloadResponse(response.data, response.networkResponse, true);
  downloadFile(download.blob, download.fileName);
}

export async function getReportNotesFromReporting(
  reportName: string
): Promise<MappedResponse<ExportResponse>> {
  const response = await useDownload<Blob>(API_ROUTES.Reports.GetReportNotes(reportName), {
    headers: {
      [RequestHeaders.UseReportingUrl]: 'true'
    }
  });
  if (response.status === RequestStatus.ERROR) {
    return {
      data: null,
      status: RequestStatus.ERROR
    };
  }

  return {
    data: generateDownloadResponse(response.data, response.networkResponse, true),
    status: RequestStatus.SUCCESS
  };
}

export async function getActivitesFromReporting(
  reportName: string
): Promise<MappedResponse<ExportResponse>> {
  const response = await useDownload<string>(API_ROUTES.Reports.GetActivities(reportName), {
    headers: {
      [RequestHeaders.UseReportingUrl]: 'true'
    }
  });
  if (response.status === RequestStatus.ERROR) {
    return {
      data: null,
      status: RequestStatus.ERROR
    };
  }
  return {
    data: generateDownloadResponse(response.data, response.networkResponse, true),
    status: RequestStatus.SUCCESS
  };
}

export async function getCompressedReportFiles(
  reportName: string
): Promise<MappedResponse<ExportResponse>> {
  const response = await useDownload<ArrayBuffer>(
    API_ROUTES.Reports.GetCompressedReportFiles(reportName),
    {
      headers: { [RequestHeaders.UseReportingUrl]: 'true' },
      responseType: 'arraybuffer'
    }
  );
  if (response.status === RequestStatus.ERROR) {
    return {
      data: null,
      status: RequestStatus.ERROR
    };
  }
  return {
    data: generateDownloadResponse(response.data, response.networkResponse),
    status: RequestStatus.SUCCESS
  };
}

export async function getReportSelectionByPeriodType(
  reportPeriodType: 'active' | 'historic'
): Promise<Report[]> {
  const response = await useGet<ReportListResponse>(
    API_ROUTES.Reports.GetSelection(reportPeriodType)
  );
  return response.data?.reports || [];
}

export async function addToSelection(
  addReportIds: number[],
  reportsListPeriodType: 'active' | 'historic',
  reportsListType?: ReportsType
): Promise<MappedResponse<SelectionChangeResponse>> {
  const response = await usePut<SelectionChangeResponse, AddToSelection>(
    API_ROUTES.Reports.ToggleReportSelection,
    {
      addReportIds,
      reportsListPeriodType,
      reportsListType
    }
  );
  return unpackMappedResponse(response);
}

export async function removeFromSelection(
  removeReportIds: number[],
  reportsListPeriodType: 'active' | 'historic',
  reportsListType?: ReportsType
): Promise<MappedResponse<SelectionChangeResponse>> {
  const response = await usePut<SelectionChangeResponse, RemoveFromSelection>(
    API_ROUTES.Reports.ToggleReportSelection,
    {
      removeReportIds,
      reportsListPeriodType,
      reportsListType
    }
  );
  return unpackMappedResponse(response);
}

export async function clearSelector(
  reportsListPeriodType: 'active' | 'historic'
): Promise<MappedResponse<SelectionChangeResponse>> {
  const response = await useDelete<SelectionChangeResponse>(
    API_ROUTES.Reports.ClearSelector(reportsListPeriodType)
  );
  return unpackMappedResponse(response);
}

export async function addSelectionFromCurrentFilter(
  reportsListPeriodType: 'active' | 'historic',
  reportsListType: ReportsType,
  filters: GetReportsRequest
): Promise<MappedResponse<SelectionChangeResponse>> {
  const params = generateParams(filters);
  const url = API_ROUTES.Reports.ToggleReportSelection + '?' + params.toString();
  const response = await usePut<SelectionChangeResponse, ChangeSelection>(url, {
    addReportIds: [],
    removeReportIds: [],
    reportsListType,
    reportsListPeriodType
  });
  return unpackMappedResponse(response);
}

export async function setForCorrection(reportName: string): Promise<MappedResponse<ReportDetails>> {
  const response = await usePut<ReportDetails, null>(
    API_ROUTES.Reports.SetReportForCorrection(reportName),
    null
  );
  return unpackMappedResponse(response);
}

export async function createReport(
  report: ImportReport
): Promise<MappedResponse<CreateReportResponse>> {
  const response = await usePost<CreateReportResponse, ImportReport>(
    API_ROUTES.Reports.CreateReport,
    report
  );
  return unpackMappedResponse(response);
}

export async function getReportsWithDueDates(): Promise<DueDateReport[]> {
  const response = await useGet<{ reportsDueDate: DueDateReport[] }>(
    API_ROUTES.Reports.GetReportsWithDueDates
  );
  return response.data?.reportsDueDate || [];
}

export async function approveReport(
  payload: ApproveReport
): Promise<MappedResponse<ReportDetails>> {
  const data = new FormData();
  data.append('file', payload.file as Blob);
  data.append('description', payload.description);
  data.append('title', payload.title);
  data.append('extension', payload.extension);
  data.append('size', payload.size.toString());
  data.append('reportName', payload.reportName);

  const response = await usePut<{ report: ReportDetails }, FormData>(
    API_ROUTES.Reports.ApproveReport(payload.reportName),
    data,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );
  return unpackMappedResponseWithKey(response, 'report');
}

export async function cancelApproval(reportName: string): Promise<MappedResponse<ReportDetails>> {
  const response = await usePut<{ report: ReportDetails }, null>(
    API_ROUTES.Reports.CancelApproval(reportName),
    null
  );
  return unpackMappedResponseWithKey(response, 'report');
}

export async function cancelReport(reportName: string): Promise<MappedResponse<ReportDetails>> {
  const response = await usePut<{ report: ReportDetails }, null>(
    API_ROUTES.Reports.CancelReport(reportName),
    null
  );
  return unpackMappedResponseWithKey(response, 'report');
}

export async function setReportHelpingStatus(
  payload: ReportHelpingRequest
): Promise<MappedResponse<ReportDetails>> {
  const response = await usePut<{ report: ReportDetails }, ReportHelpingRequest>(
    API_ROUTES.Reports.SetReportAsHelping,
    payload
  );
  return unpackMappedResponseWithKey(response, 'report');
}

export async function createAggregatedForms(
  request: CreateAggregatedReport
): Promise<CreateAggregatedReportResponse[]> {
  const response = await usePost<
    { reports: CreateAggregatedReportResponse[] },
    CreateAggregatedReport
  >(API_ROUTES.Reports.SetAggregatedForms(request.reportName), request);
  return response.data?.reports || [];
}

export async function updateReport<R = CreateReportResponse>(
  reportName: string,
  payload: UpdateReportPayload
): Promise<MappedResponse<R>> {
  const response = await usePut<R, UpdateReportPayload>(
    API_ROUTES.Reports.UpdateReport(reportName),
    payload
  );
  return unpackMappedResponse(response);
}

export async function getAopCalculation(
  reportName: string,
  code: number
): Promise<MappedResponse<AopCalculation>> {
  const response = await useGet<AopCalculationResponse>(
    API_ROUTES.Reports.GetAopCalculation(reportName, code)
  );
  return unpackMappedResponseWithKey(response, 'calculation');
}

export async function createProject(
  reportName: string,
  project: ProjectForm
): Promise<MappedResponse<{ projectAopForm: ReportForm; projectJsonForm: ReportForm }>> {
  const response = await usePost<{ projectAopForm: ReportForm; projectJsonForm: ReportForm }, ProjectForm>(
    API_ROUTES.Reports.AddReportProject(reportName),
    project
  );
  return unpackMappedResponse<{ projectAopForm: ReportForm; projectJsonForm: ReportForm }>(response);
}

export async function updateProject(
  reportName: string,
  project: ProjectForm
): Promise<MappedResponse<ModifyProjectResponse>> {
  const response = await usePut<ModifyProjectResponse, ProjectForm>(
    API_ROUTES.Reports.UpdateReportProject(reportName, project.formId),
    project
  );
  return unpackMappedResponse(response);
}

export async function deleteProject(
  reportName: string,
  formId: number
): Promise<MappedResponse<ReportForm>> {
  const response = await useDelete<{ project: ReportForm }>(
    API_ROUTES.Reports.DeleteReportProject(reportName, formId)
  );
  return unpackMappedResponseWithKey(response, 'project');
}

export async function getAopValidationValues(
  reportName: string,
  code: string
): Promise<AopValidationValues[]> {
  const response = await useGet<AopValidationValuesResponse>(
    API_ROUTES.Reports.GetAopValidationValues(reportName, code)
  );
  return response.data?.values || [];
}

export async function getReportNotes(
  reportName: string,
  sortDesc: SortDirection
): Promise<ReportNote[]> {
  const response = await useGetPaged<GetReportNotesResponse, PaginatedRequest>(
    API_ROUTES.Reports.GetReportNotes(reportName),
    {
      page: 1,
      perPage: 100,
      sortDesc
    }
  );
  return response.data?.notes || [];
}

export async function createReportNote(
  reportName: string,
  note: string
): Promise<MappedResponse<ReportNote>> {
  const response = await usePost<ReportNoteResponse, CreateReportNoteRequest>(
    API_ROUTES.Reports.GetReportNotes(reportName),
    {
      reportName,
      note
    }
  );
  return unpackMappedResponseWithKey(response, 'note');
}

export async function updateReportNote(
  reportName: string,
  noteId: number,
  note: string
): Promise<MappedResponse<ReportNote>> {
  const response = await usePut<ReportNoteResponse, EditReportNoteRequest>(
    API_ROUTES.Reports.UpdateReportNote(reportName, noteId),
    {
      note
    }
  );
  return unpackMappedResponseWithKey(response, 'note');
}

export async function restrictReportNote(
  payload: RestrictNoteRequest
): Promise<MappedResponse<ReportNote>> {
  const response = await usePut<ReportNoteResponse, RestrictNoteRequest>(
    API_ROUTES.Reports.RestrictReportNote(payload.reportName, payload.noteId),
    payload
  );
  return unpackMappedResponseWithKey(response, 'note');
}

export async function getJsonFormHistory(
  reportName: string,
  formId: number,
  positionId: number,
  editableFields: (keyof EoForm)[] = []
): Promise<JsonFormHistory[]> {
  const response = await useGet<JsonFormHistoryResponse>(API_ROUTES.Reports.GetJsonFormHistory(reportName, formId, positionId));
  const result = unpackMappedResponseWithKey(response, 'jsonFormPositionsHistoryResponses');
  const mapped = !result.data
    ? []
    : result.data.map((history: JsonFormHistory): JsonFormHistory => ({
        ...history,
        data: parseJson<EoForm>(history.jsonData) as EoForm
      }));
  return filterDuplicatesFromHistory(mapped, editableFields);
}

export function filterDuplicatesFromHistory(
  histories: JsonFormHistory[],
  editableFields: (keyof EoForm)[]
): JsonFormHistory[] {
  if (!histories?.length) {
    return [];
  }
  const filtered = [histories[0]];
  histories.forEach((history: JsonFormHistory, i: number): void => {
    if (!i) {
      return;
    }
    const sameAsFirst = compareObjectsByFields(filtered[0].data, history.data, editableFields);
    const sameAsPrevious = compareObjectsByFields(histories[i - 1].data, history.data, editableFields);
    if (sameAsFirst || sameAsPrevious) {
      return;
    }
    filtered.push(history);
  });
  return filtered;
}

export async function updateJsonForm(
  reportName: string,
  formId: number,
  positionId: number,
  jsonForm: UpdateReportJsonForm
): Promise<MappedResponse<null>> {
  const response = await usePut<null, UpdateReportJsonForm>(API_ROUTES.Reports.UpdateJsonForm(reportName, formId, positionId), jsonForm);
  return unpackMappedResponse(response);
}

export async function downloadConsolidatedReportForReportTypeId(reportTypePeriodId: number): Promise<void> {
  const response = await useDownload<string, GetActivitiesRequest>(
    API_ROUTES.Reports.DownloadConsolidatedReportByReportTypeId(reportTypePeriodId)
  );
  if (response.status === RequestStatus.ERROR) {
    return;
  }
  const download = generateDownloadResponse(response.data, response.networkResponse, false);
  downloadFile(download.blob, download.fileName);
}
